import { useCallback, useEffect, useRef, useState } from 'react';

import {
  api,
  type BetoltesAllapot,
  type EtteremLista,
  type EtteremSor,
  type ForgalomElonezet,
  type ForgalomEredmeny,
  type SzerzodesElonezet,
  type SzerzodesImportEredmeny,
} from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Adatbetöltés a meglévő Excelekből. (Megbízás 2.0 / 6.)
 *
 * Három betöltés, mindegyik ugyanazzal a menettel:
 *
 *   1. kiválasztod a fájlt
 *   2. **előnézet** — megmutatja, mit talált és mihez párosította; nem ír semmit
 *   3. a párosítatlanokat kézzel hozzárendeled
 *   4. betöltés
 *
 * Mindegyik **újrafuttatható**: ugyanazt a fájlt kétszer betöltve ugyanaz az
 * eredmény, nem duplázódik semmi. A kézi párosításokat a program megjegyzi,
 * így a következő betöltésnél magától felismeri őket.
 */

type Fajta = 'mo' | 'fo' | 'forgalom';

const FAJTA_ADATOK: Record<Fajta, { cim: string; leiras: string; fajl: string }> = {
  mo: {
    cim: 'MO szerződések',
    leiras:
      'A moslékszerződések listája. Egy étteremhez a legfrissebb kötésű szerződés kerül be, ' +
      'akkor is, ha az már lejárt — a lejártakból lesz az „újrakötendő” teendő.',
    fajl: 'MO szerződések MHB.xlsx',
  },
  fo: {
    cim: 'HSO szerződések',
    leiras:
      'A fáradtolaj-szerződések listája, a szerződés és a nyilatkozat meglétével. ' +
      'A fájlbeli ÁR csak a szerződésbe kerül; az elszámolás mindig a Termékek törzs HSO árával megy.',
    fajl: 'FO szerződések MHB állati.xlsx',
  },
  forgalom: {
    cim: 'Forgalmi előzmény',
    leiras:
      'A régi havi bontás literadata. Ebben csak mennyiség van — ár, sofőr, fizetési mód nincs —, ' +
      'ezért nem lezárt napok lesznek belőle, hanem külön jelölt előzmény.',
    fajl: 'Havibontás 2026.xlsx',
  },
};

export function Adatbetoltes({ onUgras }: { onUgras?: (kepernyo: string) => void }) {
  const [allapot, setAllapot] = useState<BetoltesAllapot | null>(null);
  const [nyitott, setNyitott] = useState<Fajta | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  async function kodokKiosztasa() {
    if (
      !confirm(
        'Ez minden éttermet ellát végleges partnerkóddal, abc sorrendben.\n' +
          'Egyszeri és végleges: innentől a kódok a szerződésszámok részei.\n' +
          'Éles indulás előtt futtasd, ne a teszt alatt. Folytatod?',
      )
    )
      return;
    try {
      const v = await api.post<{ etterem: number; szerzodes: number }>('/api/szerzodes/kodok-kiosztasa');
      setUzenet(`${v.etterem} étterem kapott partnerkódot.`);
      await allapotBetolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A kiosztás nem sikerült.');
    }
  }

  const allapotBetolt = useCallback(async () => {
    try {
      setAllapot(await api.get<BetoltesAllapot>('/api/betoltes/allapot'));
    } catch {
      setAllapot(null);
    }
  }, []);

  useEffect(() => {
    void allapotBetolt();
  }, [allapotBetolt]);

  const f = allapot?.forgalom;

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Adatbetöltés</span>
        <span className="hint">A meglévő Excelekből, hogy ne üresen induljunk.</span>
        <div className="stats">
          <div>
            Forgalmi előzmény
            <strong>{ft(f?.tetel ?? 0)}</strong>
          </div>
          <div>
            Liter
            <strong>{ft(f?.liter ?? 0)}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {allapot && (
        <Lepessor
          allapot={allapot}
          onKiosztas={() => void kodokKiosztasa()}
          onNyit={(fajta) => setNyitott(fajta)}
          onUgras={onUgras}
        />
      )}

      {f && f.tetel > 0 && (
        <div className="uzenet ok">
          Forgalmi előzmény: <b>{ft(f.tetel)}</b> tétel, <b>{ft(f.liter)}</b> liter,{' '}
          {ft(f.ettermek)} étterem, {f.legkorabbi} és {f.legkesobbi} között.
        </div>
      )}

      {(Object.keys(FAJTA_ADATOK) as Fajta[]).map((fajta) => (
        <BetoltoKartya
          key={fajta}
          fajta={fajta}
          nyitva={nyitott === fajta}
          onNyit={() => setNyitott(nyitott === fajta ? null : fajta)}
          onKesz={() => void allapotBetolt()}
        />
      ))}

      <div className="uzenet flag" style={{ marginTop: 14 }}>
        <b>A betöltés nem törli, amit már beírtál.</b> A szerződéseknél a már bent lévő
        szerződést (azonos étterem, típus és szám) frissíti, az újat felveszi — a betöltött
        szerződés a saját, régi számát tartja meg. A forgalmi előzménynél napra és étteremre egy
        sor tartozik, tehát az újratöltés frissít, nem duplázza az adatot.
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * A betöltés kötött sorrendje (szerzodesek-atepites.md, 9.): a hat lépés
 * egymás alatt, az állapotukkal. Egy lépés akkor indítható, ha az ahhoz
 * szükséges adat már megvan. Minden lépés újrafuttatható, kivéve a
 * partnerkódok kiosztását — az egyszeri.
 */
function Lepessor({
  allapot,
  onKiosztas,
  onNyit,
  onUgras,
}: {
  allapot: BetoltesAllapot;
  onKiosztas: () => void;
  onNyit: (fajta: Fajta) => void;
  onUgras?: (kepernyo: string) => void;
}) {
  const l = allapot.lepesek;
  const torzs = l.etterem > 0;
  const nincsTorzs = 'előbb a partnertörzs';

  const sorok: Array<{
    cim: string;
    fajl: string;
    kesz: boolean;
    allapot: string;
    gomb: React.ReactNode;
    figyelem?: string;
  }> = [
    {
      cim: 'Partnertörzs',
      fajl: 'NAPI_CÍMLISTA_2026_új.xltm — Adatbazis fül',
      kesz: torzs,
      allapot: torzs ? `betöltve, ${ft(l.etterem)} étterem` : 'nincs betöltve',
      gomb: <span className="hint">a telepítéskor töltődik be — ebből él minden más</span>,
    },
    {
      cim: 'Partnerkódok kiosztása',
      fajl: 'abc sorrendben, sorfolytonosan',
      kesz: l.kodok.kiosztva && l.kodok.kod_nelkul === 0,
      allapot: l.kodok.kiosztva ? `kiosztva, ${ft(l.kodok.kodos)} étterem` : 'nincs kiosztva',
      gomb:
        l.kodok.kiosztva && l.kodok.kod_nelkul === 0 ? null : (
          <button className="btn sm" disabled={!torzs} title={torzs ? undefined : nincsTorzs} onClick={onKiosztas}>
            Kódok kiosztása
          </button>
        ),
      figyelem: 'Egyszeri és végleges — éles induláskor futtasd, ne a teszt alatt.',
    },
    {
      cim: 'Szerződések',
      fajl: 'MO és FO külön fájlból, egymást nem felülírva',
      kesz: l.szerzodes_mo + l.szerzodes_fo > 0,
      allapot: `MO: ${ft(l.szerzodes_mo)} · FO: ${ft(l.szerzodes_fo)}`,
      gomb: (
        <>
          <button className="btn sm" disabled={!torzs} onClick={() => onNyit('mo')}>
            MO betöltése
          </button>{' '}
          <button className="btn sm" disabled={!torzs} onClick={() => onNyit('fo')}>
            FO betöltése
          </button>
        </>
      ),
    },
    {
      cim: 'Számlák',
      fajl: 'Átutalás_MHB_2026 — Kimutatás lap',
      kesz: l.szamla > 0,
      allapot: l.szamla > 0 ? `${ft(l.szamla)} számla` : 'nincs betöltve',
      gomb: onUgras ? (
        <button className="btn sm" disabled={!torzs} onClick={() => onUgras('szamlak')}>
          Számlák képernyő
        </button>
      ) : null,
    },
    {
      cim: 'Bankkivonatok',
      fajl: 'CAMT.053 vagy MT940 — a számlákhoz párosít',
      kesz: l.kivonat > 0,
      allapot: l.kivonat > 0 ? `${ft(l.kivonat)} kivonat` : 'nincs betöltve',
      gomb: onUgras ? (
        <button
          className="btn sm"
          disabled={l.szamla === 0}
          title={l.szamla === 0 ? 'előbb a számlák' : undefined}
          onClick={() => onUgras('bank')}
        >
          Bankkivonatok
        </button>
      ) : null,
    },
    {
      cim: 'Forgalmi előzmény',
      fajl: 'Havibontás_2026.xlsx',
      kesz: allapot.forgalom.tetel > 0,
      allapot: allapot.forgalom.tetel > 0 ? `${ft(allapot.forgalom.tetel)} tétel` : 'nincs betöltve',
      gomb: (
        <button className="btn sm" disabled={!torzs} onClick={() => onNyit('forgalom')}>
          Betöltés
        </button>
      ),
    },
  ];

  return (
    <div className="matrix" style={{ marginBottom: 14 }}>
      <table className="tt2">
        <thead>
          <tr>
            <th style={{ width: 28 }}>#</th>
            <th>Lépés</th>
            <th style={{ width: 230 }}>Állapot</th>
            <th style={{ width: 260 }}></th>
          </tr>
        </thead>
        <tbody>
          {sorok.map((s, i) => (
            <tr key={s.cim}>
              <td className="calc2" style={{ textAlign: 'center' }}>
                {i + 1}.
              </td>
              <td style={{ padding: '6px 10px' }}>
                <b>{s.cim}</b>
                <div className="halvany" style={{ fontSize: 11 }}>
                  {s.fajl}
                </div>
                {s.figyelem && (
                  <div style={{ fontSize: 11, color: 'var(--flag)', fontWeight: 600 }}>{s.figyelem}</div>
                )}
              </td>
              <td style={{ padding: '6px 10px', color: s.kesz ? 'var(--ok)' : 'var(--muted)' }}>
                {s.kesz ? '✓ ' : ''}
                {s.allapot}
              </td>
              <td className="act" style={{ whiteSpace: 'nowrap' }}>
                {s.gomb}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */

type Lepes = 'ures' | 'elonezet' | 'kesz';

function BetoltoKartya({
  fajta,
  nyitva,
  onNyit,
  onKesz,
}: {
  fajta: Fajta;
  nyitva: boolean;
  onNyit: () => void;
  onKesz: () => void;
}) {
  const adat = FAJTA_ADATOK[fajta];
  const [lepes, setLepes] = useState<Lepes>('ures');
  const [azonosito, setAzonosito] = useState<string | null>(null);
  const [fajlnev, setFajlnev] = useState('');
  const [ev, setEv] = useState(String(new Date().getFullYear()));
  const [munkaban, setMunkaban] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [szerzodes, setSzerzodes] = useState<SzerzodesElonezet | null>(null);
  const [forgalom, setForgalom] = useState<ForgalomElonezet | null>(null);
  /** Fájlbeli név → kézzel választott étterem. */
  const [parositasok, setParositasok] = useState<Record<string, number>>({});
  const fajlMezo = useRef<HTMLInputElement>(null);

  function alaphelyzet() {
    setLepes('ures');
    setAzonosito(null);
    setFajlnev('');
    setSzerzodes(null);
    setForgalom(null);
    setParositasok({});
    setHiba(null);
    setUzenet(null);
  }

  async function fajlValasztva(fajl: File) {
    setMunkaban(true);
    setHiba(null);
    setUzenet(null);
    try {
      const feltoltve = await api.fajl<{ azonosito: string }>('/api/betoltes/feltoltes', fajl);
      setAzonosito(feltoltve.azonosito);
      setFajlnev(fajl.name);

      // Az év a fájlnévből: `Havibontás 2026.xlsx`.
      const evMinta = /(20\d{2})/.exec(fajl.name);
      const valasztottEv = evMinta ? evMinta[1]! : ev;
      setEv(valasztottEv);

      if (fajta === 'forgalom') {
        setForgalom(
          await api.post<ForgalomElonezet>('/api/betoltes/forgalom/elonezet', {
            azonosito: feltoltve.azonosito,
            ev: Number(valasztottEv),
          }),
        );
      } else {
        setSzerzodes(
          await api.post<SzerzodesElonezet>('/api/betoltes/szerzodes/elonezet', {
            azonosito: feltoltve.azonosito,
            fajta,
          }),
        );
      }
      setLepes('elonezet');
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A fájlt nem sikerült beolvasni.');
      setAzonosito(null);
    } finally {
      setMunkaban(false);
      if (fajlMezo.current) fajlMezo.current.value = '';
    }
  }

  async function betolt() {
    if (!azonosito) return;
    setMunkaban(true);
    setHiba(null);
    try {
      const lista = Object.entries(parositasok).map(([nev, etterem_id]) => ({ nev, etterem_id }));

      if (fajta === 'forgalom') {
        const e = await api.post<ForgalomEredmeny>('/api/betoltes/forgalom/vegrehajtas', {
          azonosito,
          ev: Number(ev),
          parositasok: lista,
        });
        setUzenet(
          `Kész: ${ft(e.beirt)} új tétel, ${ft(e.frissitett)} frissítve, ${ft(e.valtozatlan)} változatlan, összesen ` +
            `${ft(e.liter)} liter. ${e.kihagyott_tetel > 0 ? `${ft(e.kihagyott_tetel)} tétel kimaradt párosítatlan név miatt.` : ''}` +
            `${e.uj_alias > 0 ? ` ${e.uj_alias} új névpárosítást megjegyeztem.` : ''}`,
        );
      } else {
        const e = await api.post<SzerzodesImportEredmeny>('/api/betoltes/szerzodes/vegrehajtas', {
          azonosito,
          fajta,
          parositasok: lista,
        });
        setUzenet(
          `Kész: ${ft(e.erintett)} étteremhez került szerződés` +
            (fajta === 'mo' ? ` (${ft(e.elo)} élő, ${ft(e.lejart)} lejárt)` : '') +
            `.${e.uj_alias > 0 ? ` ${e.uj_alias} új névpárosítást megjegyeztem.` : ''}`,
        );
      }
      setLepes('kesz');
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A betöltés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  /** A párosítatlan nevek — itt kell dönteni. */
  const parositando =
    fajta === 'forgalom'
      ? (forgalom?.nevek ?? [])
          .filter((n) => n.etterem_id === null)
          .map((n) => ({
            kulcs: n.nev,
            cim: n.nev,
            alcim: `${ft(n.tetelek)} tétel · ${ft(n.liter)} liter`,
            jeloltek: n.jeloltek,
          }))
      : egyediParositando(szerzodes);

  const megoldva = parositando.filter((p) => parositasok[p.kulcs] !== undefined).length;

  return (
    <div className={`betolto${nyitva ? ' nyitva' : ''}`}>
      <button className="bfej" onClick={onNyit}>
        <span className="bcim">{adat.cim}</span>
        <span className="bfajl">{adat.fajl}</span>
        <span className="bjel">{nyitva ? '−' : '+'}</span>
      </button>

      {nyitva && (
        <div className="btest">
          <p className="hint" style={{ margin: '0 0 10px' }}>
            {adat.leiras}
          </p>

          {hiba && (
            <div className="uzenet bad" onClick={() => setHiba(null)}>
              {hiba}
            </div>
          )}
          {uzenet && <div className="uzenet ok">{uzenet}</div>}

          {lepes === 'ures' && (
            <div className="btns">
              <input
                ref={fajlMezo}
                type="file"
                accept=".xlsx,.xlsm,.xltm"
                disabled={munkaban}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void fajlValasztva(f);
                }}
              />
              {munkaban && <span className="hint">Beolvasás…</span>}
            </div>
          )}

          {lepes === 'elonezet' && (
            <>
              <div className="bosszegzes">
                <span>
                  Fájl<b>{fajlnev}</b>
                </span>
                {fajta === 'forgalom' && forgalom && (
                  <>
                    <span>
                      Tétel<b>{ft(forgalom.osszegzes.tetel)}</b>
                    </span>
                    <span>
                      Liter<b>{ft(forgalom.osszegzes.liter)}</b>
                    </span>
                    <span>
                      Nap<b>{ft(forgalom.osszegzes.nap)}</b>
                    </span>
                    <span>
                      Név<b>{ft(forgalom.osszegzes.nev)}</b>
                    </span>
                    <span className={forgalom.osszegzes.parositatlan_nev > 0 ? 'gond' : undefined}>
                      Párosítatlan
                      <b>{ft(forgalom.osszegzes.parositatlan_nev)}</b>
                    </span>
                  </>
                )}
                {fajta !== 'forgalom' && szerzodes && (
                  <>
                    <span>
                      Sor a fájlban<b>{ft(szerzodes.osszegzes.fajl_sor)}</b>
                    </span>
                    <span>
                      Bekerül<b>{ft(szerzodes.osszegzes.erintett_etterem)}</b>
                    </span>
                    {fajta === 'mo' && (
                      <>
                        <span>
                          Élő<b>{ft(szerzodes.osszegzes.elo)}</b>
                        </span>
                        <span className={szerzodes.osszegzes.lejart > 0 ? 'gond' : undefined}>
                          Lejárt<b>{ft(szerzodes.osszegzes.lejart)}</b>
                        </span>
                      </>
                    )}
                    <span>
                      Felülírt régi<b>{ft(szerzodes.osszegzes.elavult)}</b>
                    </span>
                    <span className={szerzodes.osszegzes.parositatlan > 0 ? 'gond' : undefined}>
                      Párosítatlan sor<b>{ft(szerzodes.osszegzes.parositatlan)}</b>
                    </span>
                  </>
                )}
              </div>

              {fajta === 'forgalom' && (
                <label className="bev">
                  Melyik év adata?
                  <input value={ev} onChange={(e) => setEv(e.target.value)} />
                  <span className="hint">
                    A havi lapok neve csak a hónapot mondja meg, az évet nem.
                  </span>
                </label>
              )}

              {parositando.length > 0 && (
                <>
                  <h4 className="szakaszcim" style={{ marginTop: 14 }}>
                    Kézi párosítás
                    <b>
                      {ft(megoldva)} / {ft(parositando.length)}
                    </b>
                    <span className="halvany">
                      amit nem tudott magától, azt itt kösd össze — ami üresen marad, kimarad a
                      betöltésből
                    </span>
                  </h4>
                  <div className="matrix" style={{ maxHeight: 340, overflow: 'auto' }}>
                    <table className="tt2">
                      <thead>
                        <tr>
                          <th>A fájlban</th>
                          <th style={{ width: 300 }}>Melyik étterem?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parositando.map((p) => (
                          <ParositoSor
                            key={p.kulcs}
                            sor={p}
                            valasztott={parositasok[p.kulcs] ?? null}
                            onValaszt={(id) =>
                              setParositasok((elozo) => {
                                const uj = { ...elozo };
                                if (id === null) delete uj[p.kulcs];
                                else uj[p.kulcs] = id;
                                return uj;
                              })
                            }
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className="btns">
                <button className="btn gold sm" disabled={munkaban} onClick={() => void betolt()}>
                  {munkaban ? 'Betöltés…' : 'Betöltés'}
                </button>
                <button className="btn sm" disabled={munkaban} onClick={alaphelyzet}>
                  Mégsem
                </button>
              </div>
            </>
          )}

          {lepes === 'kesz' && (
            <div className="btns">
              <button className="btn sm" onClick={alaphelyzet}>
                Új betöltés
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface ParositandoSor {
  kulcs: string;
  cim: string;
  alcim: string;
  jeloltek: Array<{ etterem_id: number; nev: string; pontszam: number }>;
}

/**
 * A szerződéslistán ugyanaz a név sokszor szerepel (minden újrakötés egy
 * sor). Elég egyszer megkérdezni, melyik étterem az — a válasz mindre áll.
 */
function egyediParositando(elonezet: SzerzodesElonezet | null): ParositandoSor[] {
  if (!elonezet) return [];
  const terkep = new Map<string, ParositandoSor & { sorok: number }>();

  for (const s of elonezet.sorok) {
    if (s.etterem_id !== null) continue;
    const kulcs = s.egyseg || s.cegnev;
    if (!kulcs) continue;
    const meglevo = terkep.get(kulcs);
    if (meglevo) {
      meglevo.sorok++;
      continue;
    }
    terkep.set(kulcs, {
      kulcs,
      cim: kulcs,
      alcim: [s.cegnev !== kulcs ? s.cegnev : null, s.adoszam, s.cim]
        .filter(Boolean)
        .join(' · '),
      jeloltek: s.jeloltek,
      sorok: 1,
    });
  }

  return [...terkep.values()]
    .sort((a, b) => b.sorok - a.sorok || a.cim.localeCompare(b.cim, 'hu'))
    .map(({ kulcs, cim, alcim, jeloltek, sorok }) => ({
      kulcs,
      cim,
      alcim: sorok > 1 ? `${alcim} · ${sorok} sor` : alcim,
      jeloltek,
    }));
}

function ParositoSor({
  sor,
  valasztott,
  onValaszt,
}: {
  sor: ParositandoSor;
  valasztott: number | null;
  onValaszt: (etterem_id: number | null) => void;
}) {
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [valasztottNev, setValasztottNev] = useState<string | null>(null);

  useEffect(() => {
    const q = kereses.trim();
    if (q.length < 2) {
      setTalalatok([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const e = await api.get<EtteremLista>(
          `/api/etterem?q=${encodeURIComponent(q)}&limit=12`,
        );
        setTalalatok(e.sorok);
      } catch {
        setTalalatok([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [kereses]);

  return (
    <tr className={valasztott !== null ? 'kijelolt' : undefined}>
      <td className="nm3">
        <b>{sor.cim}</b>
        {sor.alcim && <small>{sor.alcim}</small>}
      </td>
      <td>
        {valasztott !== null ? (
          <div className="bvalasztott">
            <b>{valasztottNev ?? `#${valasztott}`}</b>
            <button
              className="xb"
              title="Párosítás visszavonása"
              onClick={() => {
                onValaszt(null);
                setValasztottNev(null);
                setKereses('');
              }}
            >
              ↩
            </button>
          </div>
        ) : (
          <>
            {sor.jeloltek.length > 0 && (
              <div className="bjeloltek">
                {sor.jeloltek.slice(0, 4).map((j) => (
                  <button
                    key={j.etterem_id}
                    className="btn sm"
                    onClick={() => {
                      onValaszt(j.etterem_id);
                      setValasztottNev(j.nev);
                    }}
                  >
                    {j.nev}
                  </button>
                ))}
              </div>
            )}
            <input
              className="emin"
              placeholder="vagy keress rá egy étteremre…"
              value={kereses}
              onChange={(e) => setKereses(e.target.value)}
            />
            {talalatok.length > 0 && (
              <div className="bjeloltek">
                {talalatok.slice(0, 6).map((t) => (
                  <button
                    key={t.id}
                    className="btn sm"
                    onClick={() => {
                      onValaszt(t.id);
                      setValasztottNev(t.nev);
                      setKereses('');
                      setTalalatok([]);
                    }}
                  >
                    {t.nev}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
